import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartArea, Lock } from "lucide-react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavbar } from "./DashboardLayout";
import RowTracker from "./rowTracker";
import TrackerBoard from "../components/TrackerBoard";
import SankeyModal from "../components/SankeyModal";
import IconButtonWithTooltip from "../components/IconButtonWithTooltip";
import { HEADER_ICON_BUTTON, TrackerSearch, ViewMode, ViewModeToggle } from "../components/trackerHeader";
import type { ArchiveSummary } from "../components/archiveCycleModal";
import { ApiItem, matchesQuery, toApplications } from "../utils/applicationCards";
import { cn } from "../lib/utils";

type ArchiveDoc = {
  name: string;
  count: number;
  createdAt: Date | null;
  summary: ArchiveSummary | null;
};

// "2026-01-10" -> "Jan 10, 2026", read as a local date so it doesn't shift a day.
function formatDay(iso?: string | null) {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Read-only view of one archived recruiting cycle: the same board / rows /
// Sankey as the Tracker, over cards whose archiveId is this archive.
export default function ArchiveView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setNavbarActions } = useNavbar();

  const [archive, setArchive] = useState<ArchiveDoc | null>(null);
  const [data, setData] = useState<ApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem("viewMode") as ViewMode) || "columns"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showSankey, setShowSankey] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      if (!id) return;
      try {
        const archiveSnap = await getDoc(doc(db, "users", user.uid, "archives", id));
        if (!archiveSnap.exists()) {
          setNotFound(true);
          return;
        }
        const a = archiveSnap.data();
        setArchive({
          name: a.name || "Untitled archive",
          count: a.count ?? 0,
          createdAt: a.createdAt?.toDate ? a.createdAt.toDate() : null,
          summary: a.summary ?? null,
        });

        const cardsSnap = await getDocs(
          query(collection(db, "users", user.uid, "emails"), where("archiveId", "==", id))
        );
        setData(toApplications(cardsSnap.docs));
      } catch (e: any) {
        setError(e?.message ?? "Failed to load this archive");
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [id, navigate]);

  // Title and tools sit in the top bar, like the tracker.
  useEffect(() => {
    setNavbarActions(
      <div className="flex w-full min-w-0 items-center justify-between gap-4 font-grotesk">
        <div className="flex min-w-0 items-center gap-2">
          <IconButtonWithTooltip
            label="Back to Archives"
            onClick={() => navigate("/settings/archives")}
            className={cn(HEADER_ICON_BUTTON, "-ml-1.5")}
          >
            <ArrowLeft size={17} strokeWidth={1.75} />
          </IconButtonWithTooltip>
          <div className="flex min-w-0 items-baseline gap-3">
            <h1 className="truncate text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">
              {archive?.name ?? "Archive"}
            </h1>
            <div className="hidden items-center gap-1.5 whitespace-nowrap font-plex text-xs text-melt-text3 sm:flex">
              <Lock size={11} strokeWidth={1.75} />
              Read-only{archive && <> · {archive.count} archived</>}
            </div>
          </div>
        </div>

        {archive && (
          <div className="flex items-center gap-2.5">
            <TrackerSearch onQueryChange={setSearchQuery} />
            <IconButtonWithTooltip label="Sankey chart" onClick={() => setShowSankey(true)} className={HEADER_ICON_BUTTON}>
              <ChartArea size={17} strokeWidth={1.75} />
            </IconButtonWithTooltip>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
        )}
      </div>
    );
  }, [archive, viewMode, navigate, setNavbarActions]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  const filteredData = useMemo(
    () => data.filter((it) => matchesQuery(it, searchQuery)),
    [data, searchQuery]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 font-grotesk">
        <div className="text-melt-text3">Loading...</div>
      </div>
    );
  }

  if (notFound || error) {
    return (
      <div className="mx-auto w-full max-w-3xl font-grotesk">
        <div className="rounded-[10px] bg-[#F7E6DE] px-4 py-3 text-sm text-[#9A4F33]">
          {notFound ? "This archive doesn't exist anymore." : error}
        </div>
      </div>
    );
  }

  const summary = archive?.summary;
  const first = formatDay(summary?.firstApplicationDate);
  const last = formatDay(summary?.lastApplicationDate);
  const stats = summary
    ? [
        { label: "Applications", value: summary.count },
        { label: "Interviews", value: summary.reached.interview },
        { label: "Offers", value: summary.reached.offer },
        { label: "Response rate", value: `${Math.round(summary.responseRate * 100)}%` },
      ]
    : [];

  return (
    <div
      className={`w-full font-grotesk ${
        viewMode === "columns"
          ? "md:flex md:min-h-0 md:flex-col md:overflow-x-hidden md:h-[calc(100%_+_2rem)] md:-mb-8"
          : ""
      }`}
    >
      {/* Cycle summary */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <p className="font-plex text-xs text-melt-text3">
          {first && last ? `Applied ${first === last ? `on ${first}` : `${first} – ${last}`}` : "Archived cycle"}
          {archive?.createdAt &&
            ` · Archived ${archive.createdAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
        </p>
        {stats.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {stats.map((s) => (
              <div key={s.label} className="flex items-baseline gap-2 rounded-[10px] border border-melt-hair bg-white px-3 py-2">
                <span className="font-plex text-[15px] font-medium text-melt-text1">{s.value}</span>
                <span className="text-xs text-melt-text3">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewMode === "rows" ? (
        <RowTracker data={data} onUpdate={() => {}} query={searchQuery} readOnly />
      ) : (
        <TrackerBoard data={filteredData} readOnly searching={!!searchQuery.trim()} />
      )}

      <SankeyModal isOpen={showSankey} onClose={() => setShowSankey(false)} apps={data} />
    </div>
  );
}
